import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { MAX_BOTTLES, priceFor, stackLabel, stackKey } from "../lib/pricing";

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const load = (k, d) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load("tw_cart", {})); // {slug: qty}
  const [pickup, setPickup] = useState(() => load("tw_pickup", null));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [catMap, setCatMap] = useState(null); // {slug: "functional"|"traditional"}
  const [nameMap, setNameMap] = useState({});

  useEffect(() => {
    api.get("/products").then((r) => {
      setCatMap(Object.fromEntries(r.data.map((p) => [p.slug, p.category || "functional"])));
      setNameMap(Object.fromEntries(r.data.map((p) => [p.slug, p.name])));
    }).catch(() => {});
  }, []);
  useEffect(() => localStorage.setItem("tw_cart", JSON.stringify(items)), [items]);
  useEffect(() => localStorage.setItem("tw_pickup", JSON.stringify(pickup)), [pickup]);

  const count = useMemo(() => Object.values(items).reduce((a, b) => a + b, 0), [items]);
  const split = useMemo(() => {
    if (!catMap) return null;
    let nf = 0, nt = 0;
    for (const [slug, qty] of Object.entries(items)) {
      if (catMap[slug] === "traditional") nt += qty; else nf += qty;
    }
    return { functional: nf, traditional: nt,
             functionalCents: nf > 0 ? priceFor(nf, 0) : 0, traditionalCents: priceFor(0, nt) };
  }, [items, catMap]);
  const bundle = useMemo(() => {
    if (count < 1 || count > MAX_BOTTLES || !split) return null;
    const label = split.traditional > 0 && split.functional > 0
      ? `${count} Bottles`
      : split.traditional > 0 ? `${count} Bottle${count > 1 ? "s" : ""} (traditional)` : stackLabel(count);
    return { key: stackKey(count), cents: split.functionalCents + split.traditionalCents, label };
  }, [count, split]);

  const add = (slug, qty = 1) => setItems((p) => {
    const others = Object.entries(p).reduce((a, [s, q]) => a + (s === slug ? 0 : q), 0);
    const next = Math.min(MAX_BOTTLES - others, (p[slug] || 0) + qty);
    return next <= 0 ? p : { ...p, [slug]: next };
  });
  const setQty = (slug, qty) => setItems((p) => {
    const n = { ...p };
    const others = Object.entries(p).reduce((a, [s, q]) => a + (s === slug ? 0 : q), 0);
    const capped = Math.min(MAX_BOTTLES - others, qty);
    if (capped <= 0) delete n[slug]; else n[slug] = capped;
    return n;
  });
  const replace = (map) => setItems(map);
  const clear = () => { setItems({}); setPickup(null); };

  return (
    <CartCtx.Provider value={{ items, count, bundle, split, catMap, nameMap, pickup, setPickup, add, setQty, replace, clear, drawerOpen, setDrawerOpen }}>
      {children}
    </CartCtx.Provider>
  );
}
